import apiClient from '../../authentication/utils/core/axios';
import { format, subDays, subMonths, subYears } from 'date-fns';
import qs from 'qs';

/**
 * Fetches historical stock data with technical indicators
 * 
 * @param {string} ticker - Stock symbol/ticker
 * @param {string} timeframe - Data aggregation period ('daily', 'weekly', 'monthly', 'yearly')
 * @param {Date|string|null} startDate - Start date for data range
 * @param {Date|string|null} endDate - End date for data range
 * @param {string[]} indicators - Technical indicators to calculate
 * @returns {Promise<Object>} - Stock data with indicators
 * @throws {Error} - If API request fails
 */
export const getStockData = async (
  ticker,
  timeframe = 'daily',
  startDate = null,
  endDate = null,
  indicators = []
) => {
  if (!ticker) {
    throw new Error('Stock ticker symbol is required');
  }
  
  // Validate timeframe
  const validTimeframes = ['daily', 'weekly', 'monthly', 'yearly'];
  if (!validTimeframes.includes(timeframe)) {
    throw new Error(`Invalid timeframe: ${timeframe}. Must be one of: ${validTimeframes.join(', ')}`);
  }

  // Set appropriate default date ranges based on timeframe
  const now = new Date();
  const end = endDate ? format(new Date(endDate), 'yyyy-MM-dd') : format(now, 'yyyy-MM-dd');
  
  let start;
  if (startDate) {
    start = format(new Date(startDate), 'yyyy-MM-dd');
  } else {
    // Default date ranges based on timeframe
    switch (timeframe) {
      case 'daily':
        start = format(subDays(now, 120), 'yyyy-MM-dd');
        break;
      case 'weekly':
        start = format(subMonths(now, 12), 'yyyy-MM-dd');
        break;
      case 'monthly':
        start = format(subYears(now, 3), 'yyyy-MM-dd');
        break;
      case 'yearly':
        start = format(subYears(now, 5), 'yyyy-MM-dd');
        break;
      default:
        start = format(subMonths(now, 6), 'yyyy-MM-dd');
    }
  }

  try {
    // Match the backend API's parameter naming from the FastAPI router
    const params = { 
      start, 
      end, 
      timeframe, 
      calculations: indicators 
    };
    
    // Use qs to properly format array parameters as expected by FastAPI
    const queryString = qs.stringify(params, { 
      arrayFormat: 'repeat',
      skipNulls: true
    });
    
    const url = `/stocks_charts/${encodeURIComponent(ticker.toUpperCase())}/data?${queryString}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with a status code outside the 2xx range
      const message = error.response.data?.detail || 'Failed to fetch stock data';
      throw new Error(`${message} (Status: ${error.response.status})`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('No response received from server. Please check your network connection.');
    } else {
      // Something else caused the error
      throw error;
    }
  }
};

/**
 * Get supported technical indicators for stock analysis
 * 
 * @returns {Object} Object containing available indicators with descriptions
 */
export const getAvailableIndicators = () => {
  return {
    'sma': 'Simple Moving Average',
    'ema': 'Exponential Moving Average',
    'macd': 'Moving Average Convergence Divergence',
    'bollinger_bands': 'Bollinger Bands',
    'atr': 'Average True Range',
    'rsi': 'Relative Strength Index',
    'stochastic_oscillator': 'Stochastic Oscillator',
    'adx': 'Average Directional Index',
    'obv': 'On-Balance Volume',
    'cmf': 'Chaikin Money Flow',
    'aroon': 'Aroon Indicator',
    'cci': 'Commodity Channel Index',
    'ichimoku': 'Ichimoku Cloud',
    'mfi': 'Money Flow Index',
    'parabolic_sar': 'Parabolic SAR',
    'williams_r': 'Williams %R',
    'roc': 'Rate of Change',
    'cmo': 'Chande Momentum Oscillator',
    'keltner_channels': 'Keltner Channels',
    'rvi': 'Relative Vigor Index',
    'elder_ray': 'Elder Ray Index'
  };
};


/**
 * Lightweight helper for watchlist sparklines / simple card charts.
 * Uses explicit short range and no indicators.
 *
 * @param {string} ticker
 * @param {number} days
 * @returns {Promise<Object>}
 */
export const getSimpleStockRange = async (ticker, days = 90) => {
  const now = new Date();
  const start = format(subDays(now, days), "yyyy-MM-dd");
  const end = format(now, "yyyy-MM-dd");

  return getStockData(ticker, "daily", start, end, []);
};

/**
 * Batched sibling of getSimpleStockRange -- one HTTP request for N tickers
 * instead of N. Built for watchlist sparklines (Dashboard, StockOverviewPage),
 * which previously fired one individual request per symbol via a 5-worker
 * queue (up to ~50-60 per page load). See
 * Technical_Docs/13_FIX_HISTORY.md's seventy-second pass.
 * @param {string[]} tickers
 * @param {number} days
 * @returns {Promise<Record<string, object>>}
 */
export const getSimpleStockRangeBatch = async (tickers, days = 90) => {
  if (!tickers?.length) return {};

  const response = await apiClient.get('/stocks_charts/batch', {
    params: { tickers: tickers.join(','), days },
  });
  return response.data ?? {};
};
